/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { ReportsErrorRetryCard } from './ReportsErrorRetryCard';
import { ReportsDenseSectionShell } from './ReportsDenseSectionShell';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatMoney } from '../../../shared/format/public-api';

/**
 * Resumen de aperturas y cierres de caja del período. El detalle completo
 * (quién abrió, fondo, diferencias por cierre) vive en el workspace de Caja.
 */
export function ReportsCajaTurnosSummarySection({ vm }: { vm: ReportsScreenViewModel }) {
  const sessions = vm.branchCashSessions;
  const fromServer = vm.kpiSources.cashSessions === 'server';
  const turnos = vm.intelligenceData?.turnos;
  const lastOpening = sessions.length
    ? sessions.reduce((latest, s) =>
        new Date(s.openedAt).getTime() > new Date(latest.openedAt).getTime() ? s : latest
      )
    : null;

  return (
    <ReportsDenseSectionShell>
      <Text style={styles.reportsDenseBlockTitle}>Caja y turnos</Text>
      <Text style={styles.caption}>Aperturas y cierres del período. El detalle queda en Caja.</Text>

      {vm.intelligenceLoading && !vm.intelligenceData ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="small" color={ticketColors.ink2} />
          <Text style={styles.caption}>Cargando cierres…</Text>
        </View>
      ) : vm.intelligenceError ? (
        <ReportsErrorRetryCard
          message={vm.intelligenceError}
          showRetry={vm.intelligenceErrorShowRetry}
          onRetry={() => vm.handleLoadIntelligence()}
          retryAccessibilityLabel="Reintentar cargar cierres"
        />
      ) : (
        <View style={[styles.reportsDenseListStatGrid, { marginTop: spacing.sm }]}>
          <View style={styles.reportsDenseListStatItem}>
            <Text style={styles.reportsDenseListStatItemLabel}>Aperturas</Text>
            <Text style={styles.reportsDenseListStatItemValue}>
              {fromServer ? sessions.length : '—'}
            </Text>
            <Text style={styles.caption}>
              {fromServer ? 'en el período' : 'necesita conexión'}
            </Text>
          </View>
          <View style={styles.reportsDenseListStatItem}>
            <Text style={styles.reportsDenseListStatItemLabel}>Turnos cerrados</Text>
            <Text style={styles.reportsDenseListStatItemValue}>{turnos?.turnosCount ?? 0}</Text>
          </View>
          <View style={styles.reportsDenseListStatItem}>
            <Text style={styles.reportsDenseListStatItemLabel}>Diferencia total</Text>
            <Text
              style={[
                styles.reportsDenseListStatItemValue,
                turnos && turnos.diferenciaTotal !== 0
                  ? { color: turnos.diferenciaTotal > 0 ? ticketColors.ok : ticketColors.bad }
                  : null,
              ]}
            >
              {turnos ? formatMoney(turnos.diferenciaTotal) : '—'}
            </Text>
            {turnos ? (
              <Text style={styles.caption}>
                Tasa {(turnos.tasaDiferencia * 100).toFixed(1)}%
              </Text>
            ) : null}
          </View>
        </View>
      )}

      {lastOpening ? (
        <View style={[styles.reportsDenseListFieldRow, { marginTop: spacing.xs }]}>
          <Text style={styles.reportsDenseListFieldLabel}>Última apertura</Text>
          <Text style={styles.reportsDenseListFieldValue}>
            {formatLocalDateTime(lastOpening.openedAt)} · {lastOpening.openerLabel} · fondo{' '}
            {formatMoney(lastOpening.openingAmount)}
          </Text>
        </View>
      ) : null}

      <TouchableOpacity
        style={[styles.secondaryButton, { marginTop: spacing.sm, alignSelf: 'flex-start' }]}
        onPress={vm.handleOpenCashWorkspace}
        accessibilityRole="button"
        accessibilityLabel="Ver historial en Caja"
      >
        <Text style={styles.secondaryButtonText}>Ver historial en Caja →</Text>
      </TouchableOpacity>
    </ReportsDenseSectionShell>
  );
}
